import {collection, component, EntityData, field, identifier, LocalisableEntityData, transientField} from "@fedeltapointofsale/data-client";
import {CategoryReferenceData} from "../../catalogue/category/category.data";
import {Limit_Type, Markup_Type, MarkupData} from "../markup/markup.data";
import {SurchargeDiscountInteractionData} from "../interaction/surcharge-discount-iteraction.data";

export class DiscountSellableData extends EntityData<DiscountSellableData> {
    @field sellableType: string;
    @identifier sellableId: number;
    @field name: string;
    @field discontinued: boolean;
    @field price: number;
    @field plu: string;
}

export enum DiscountType {
    FIXED_DISCOUNT,
    PERCENT_DISCOUNT,
    OPEN_FIXED_DISCOUNT,
    OPEN_PERCENT_DISCOUNT
}

export class DiscountUsageData extends EntityData<DiscountUsageData> {
    @field manualToItem: boolean = true;
    @field manualToSale: boolean = true;
    @field membershipType: boolean = true;
    @field member: boolean = true;
    @field account: boolean = true;
    @field entitlement: boolean = true;
    @field table: boolean = true;
    @field barcode: boolean = true;
}

export class DiscountReferenceData extends LocalisableEntityData<DiscountReferenceData> {
    @identifier discountId: number;
    @field name: string;
}

export class DiscountData extends LocalisableEntityData<DiscountData> {
    @identifier discountId: number;
    @field name: string;
    @field minSaleAmount: string;
    @field barcode: string;
    @field description: string;
    @field ignoreReducedPriceItems: boolean;
    @field requiresManagementPrivilege: boolean;
    @field requiresReason: boolean;
    @field includeCompositeComponents: boolean; // filter
    @component usage: DiscountUsageData;
    @field discontinued: boolean;
    @component modifier: MarkupData;
    @component category: CategoryReferenceData;
    @collection(DiscountSellableData) sellables: DiscountSellableData[];
    @collection(SurchargeDiscountInteractionData) surchargeInteractions: SurchargeDiscountInteractionData[];

    /*
     * The following transient fields map some of the persisted data to a logical view compatible with the discount edit page
     */

    private _discountAmountEnabled: boolean;
    private _minSaleAmtBefDiscEnabled: boolean;

    @transientField get discType(): DiscountType {
        if (this._discountAmountEnabled) {
            if (this.modifier != undefined && this.modifier.type != undefined && this.modifier.type == Markup_Type.FIXED_MARKDOWN) {
                return DiscountType.FIXED_DISCOUNT;
            }
            if (this.modifier != undefined && this.modifier.type != undefined && this.modifier.type == Markup_Type.PERCENT_MARKDOWN) {
                return DiscountType.PERCENT_DISCOUNT;
            }
        } else {
            if (this.modifier != undefined && this.modifier.type != undefined && this.modifier.type == Markup_Type.FIXED_MARKDOWN) {
                return DiscountType.OPEN_FIXED_DISCOUNT;
            }
            if (this.modifier != undefined && this.modifier.type != undefined && this.modifier.type == Markup_Type.PERCENT_MARKDOWN) {
                return DiscountType.OPEN_PERCENT_DISCOUNT;
            }
        }

        // default for new data
        return DiscountType.FIXED_DISCOUNT;
    }

    set discType(discType: DiscountType) {
        if (discType == undefined) {
            discType = DiscountType.PERCENT_DISCOUNT;
        }
        this._discountAmountEnabled = discType === DiscountType.FIXED_DISCOUNT || discType === DiscountType.PERCENT_DISCOUNT;
        if (!this._discountAmountEnabled) {
            this.modifier.value = null;
        }
        if (this.modifier != undefined) {
            this.modifier.type = discType === DiscountType.FIXED_DISCOUNT || discType === DiscountType.OPEN_FIXED_DISCOUNT ? Markup_Type.FIXED_MARKDOWN : Markup_Type.PERCENT_MARKDOWN;
        }
    }

    @transientField get maxDiscEnabled(): boolean {
        return this.modifier.limitType === Limit_Type.MAXIMUM;
    }

    set maxDiscEnabled(maxDiscEnabled: boolean) {
        if (maxDiscEnabled) {
            if (!this.maxDiscEnabled) {
                this.modifier.limit = null;
            }
            this.modifier.limitType = Limit_Type.MAXIMUM;
        } else if (this.maxDiscEnabled) {
            this.modifier.limitType = null;
            this.modifier.limit = null;
        }
    }

    @transientField get maxDiscValue(): number {
        return this.maxDiscEnabled ? this.modifier.limit : null;
    }

    set maxDiscValue(maxDiscValue: number) {
        if (this.maxDiscEnabled) {
            this.modifier.limit = maxDiscValue;
        }
    }

    @transientField get minPriceAftDiscEnabled(): boolean {
        return this.modifier.limitType === Limit_Type.MINIMUM;
    }

    set minPriceAftDiscEnabled(minPriceAftDiscEnabled: boolean) {
        if (minPriceAftDiscEnabled) {
            if (!this.minPriceAftDiscEnabled) {
                this.modifier.limit = null;
            }
            this.modifier.limitType = Limit_Type.MINIMUM;
        } else if (this.minPriceAftDiscEnabled) {
            this.modifier.limitType = null;
            this.modifier.limit = null;
        }
    }

    @transientField get minPriceAftDiscValue(): number {
        return this.minPriceAftDiscEnabled ? this.modifier.limit : null;
    }

    set minPriceAftDiscValue(minPriceAftDiscValue: number) {
        if (this.minPriceAftDiscEnabled) {
            this.modifier.limit = minPriceAftDiscValue;
        }
    }

    @transientField get minSaleAmtBefDiscEnabled(): boolean {
        return this._minSaleAmtBefDiscEnabled;
    }

    set minSaleAmtBefDiscEnabled(minSaleAmtBefDiscEnabled: boolean) {
        this._minSaleAmtBefDiscEnabled = minSaleAmtBefDiscEnabled;
        if (!minSaleAmtBefDiscEnabled) {
            this.minSaleAmount = null;
        }
    }

    override onLoad(): void {
        this._discountAmountEnabled = this.modifier.value != undefined;
        this._minSaleAmtBefDiscEnabled = this.minSaleAmount != undefined;
    }
}
